// -----------------------------------------------------------------------------
// Anaglyphic, Netherlands @ 2023.
// See README in the root project for more information.
// -----------------------------------------------------------------------------

import { defineStore } from "pinia";
import { PropertyStore } from "./explorerTypes";
import { useApiClient } from "@/api/client";
import DocumentVerticalDTO = App.Data.DocumentVerticalDTO;
import { t } from "@/plugins/i18n";
import DimensionDO = App.Data.DimensionDO;
import {
	LocalizedDimensionDO,
	LocalizedVerticalDO,
} from "@/modules/explorer/components/AxisConfiguration/Selection.vue";
import { useExplorerStore } from "@/modules/explorer/store";
import DocumentDO = App.Data.Explorer.DocumentDO;
import ClickhouseTableDefinitionEnum = App.Enums.ClickhouseTableDefinitionEnum;
import DataTableDO = App.Data.Explorer.DataTableDO;
import { useBaseStore } from "@/stores/base";

export function verticalHasSegmentOption(name: string): boolean {
	return ["segment_size", "retention_12m", "retention_single_cohort"].includes(
		name
	);
}

export function verticalOrDimensionSupportsLostCustomers(
	name: string
): boolean {
	return ["segment_size", "segmentcustomer"].includes(name);
}

function localizeProperty(property: DocumentVerticalDTO): LocalizedVerticalDO;
function localizeProperty(property: DimensionDO): LocalizedDimensionDO;
function localizeProperty(
	property: DocumentVerticalDTO | DimensionDO
): LocalizedVerticalDO | LocalizedDimensionDO {
	return {
		...property,
		localizedTitle: t(`properties.${property.name_clean}.title`),
		localizedDescription: t(`properties.${property.name_clean}.description`),
	};
}

function checkPropertyIsAvailable(
	property: DocumentVerticalDTO | DimensionDO | null,
	dataSources: string[],
	verticals: DocumentVerticalDTO[]
): boolean {
	if (property === null) {
		return true;
	}

	if (!("available_for_data_source" in property)) {
		// Is vertical
		return true;
	}

	if (
		verticals.some(
			(vertical) =>
				(["unique_customers", "charging_unique_contracts"].includes(
					vertical.name_clean
				) ||
					verticalHasSegmentOption(vertical.name_clean)) &&
				(property.granularity?.includes("min") ||
					property.granularity === "dayofweek")
		)
	) {
		// Unique verticals and segment size verticals only support time metrics with a date component.
		return false;
	}

	if (
		verticals.some((vertical) => verticalHasSegmentOption(vertical.name_clean))
	) {
		return (
			property.family_name_clean === "periodsubjectdate" ||
			property.name_clean === "date_comparison" ||
			property.name_clean === "fixed_for_totals"
		);
	}

	if (
		!dataSources.every((dataSource) =>
			property.available_for_data_source.includes(dataSource)
		)
	) {
		// Property not available for required data sources.
		return false;
	}

	const usedColumns = verticals.flatMap((vertical) => vertical.uses_columns);
	if (
		!property.needs_vertical_columns.every((column) =>
			usedColumns.includes(column)
		)
	) {
		// Some array join columns are required by not provided by the verticals.

		return false;
	}

	return true;
}

// -----------------------------------------------------------------------------
export const usePropertyStore = defineStore({
	id: "propertyStore",
	state: (): PropertyStore => {
		return {
			verticals: [],
			dimensions: [],
		};
	},
	actions: {
		setVerticals(verticals: DocumentVerticalDTO[]) {
			this.verticals = verticals.map((vertical) => localizeProperty(vertical));
		},
		setDimensions(dimensions: DimensionDO[]) {
			this.dimensions = dimensions.map((dimension) =>
				localizeProperty(dimension)
			);
		},
		async loadProperties(
			dimensionAvailability:
				| "is_configurable_on_axis"
				| "is_configurable_in_data_table" = "is_configurable_on_axis"
		) {
			const verticalResponse = await useApiClient("explorer").getVerticals();
			if (verticalResponse.isOk()) {
				this.setVerticals(verticalResponse.value);
			}

			const dimensionResponse = await useApiClient("explorer").getDimensions({
				[`filter[${dimensionAvailability}]`]: true,
			});
			if (dimensionResponse.isOk()) {
				this.setDimensions(dimensionResponse.value);
			}
		},
	},
	getters: {
		verticalsForGraph: (state) => {
			return state.verticals.filter(
				(vertical: DocumentVerticalDTO) => !vertical.is_metrics_only
			);
		},
		verticalsForDataTable: (state) => {
			return state.verticals.filter(
				(vertical: DocumentVerticalDTO) => !vertical.is_graph_only
			);
		},
		isAvailable: (state) => {
			return (
				property: DocumentVerticalDTO | DimensionDO | null,
				dataSources: string[] | null = null,
				axis: string = ""
			) => {
				const verticals = [useExplorerStore().document.vertical];
				if (useExplorerStore().document.secondary_vertical) {
					verticals.push(useExplorerStore().document.secondary_vertical);
				}

				if (dataSources === null) {
					if (axis === "legend") {
						dataSources = verticals[0].data_source;
					} else {
						dataSources = verticals.flatMap((vertical) => vertical.data_source);
					}
				}

				return checkPropertyIsAvailable(property, dataSources, verticals);
			};
		},
		isAvailableAsLegend: (state) => {
			return (property: DimensionDO, document: DocumentDO) => {
				// You can always use the same property on the legend as the offset.
				if (property.id === document.offset_dimension?.id) {
					return true;
				}

				// If no offset is used, a legend can be used if it's the same as the horizontal.
				if (
					property.id === document.horizontal_dimension?.id &&
					document.offset_dimension === null
				) {
					return true;
				}

				// Subject date for occupancy can't be used.
				const verticalIsOccupancyLike =
					document.vertical.name_clean.includes("occupancy") ||
					document.vertical.name_clean.includes("capacity") ||
					document.vertical.name_clean.includes("charging_power");
				if (
					property.family_name_clean === "periodsubjectdate" &&
					verticalIsOccupancyLike
				) {
					return false;
				}

				if (
					[
						"revenuesdaycap",
						"revenuestra",
						"revenuestradrivein",
						"revenuestradriveout",
						"revenuesday",
						"revenueshour",
						"lodging_trevpor",
						"lodging_trevpar",
						"charging_avg_session_duration",
						"charging_avg_session_energy",
						"charging_unique_contracts",
						"unique_vehicles",
						"unique_customers",
						"pricingfarenameshortmin",
						"pricingfarenameshortmax",
						"searchconversionpercmovementin",
						"searchconversionpercmovementex",
						"searchconversionpercoccupancyavg",
						"searchconversionpercreservationdate",
						"avg_length_of_stay_entry_date",
						"unique_transactions_spl_media",
						"revcpmavg",
					].includes(document.vertical.name_clean)
				) {
					return false;
				}

				return true;
			};
		},
		isAvailableForDataTable: (state) => {
			return (
				dataTable: DataTableDO,
				property: DocumentVerticalDTO | DimensionDO | null
			): boolean => {
				const dataSources = [
					...new Set(
						dataTable.metrics.flatMap((vertical) => vertical.data_source)
					),
				];

				return checkPropertyIsAvailable(
					property,
					dataSources,
					dataTable.metrics
				);
			};
		},
		isDocumentCompatibleWithDataSource(state) {
			return (
				document: DocumentDO,
				dataSource: ClickhouseTableDefinitionEnum
			) => {
				const dimensionsToCheck = [
					"horizontal_dimension",
					"offset_dimension",
				] as const;

				return dimensionsToCheck.every((dimensionKey) => {
					return this.isAvailable(document[dimensionKey], [dataSource]);
				});
			};
		},
		isAvailableForUser() {
			return (
				property: Pick<DocumentVerticalDTO | DimensionDO, "name_clean">
			) => {
				return useBaseStore().HAS_PERMISSION(
					`can_view_property_${property.name_clean}`
				);
			};
		},
	},
});
